import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { NLPLogo } from '../components/nlp/NLPLogo';
import { Sparkles, CheckCircle, AlertCircle, Send, Calendar, Clock, Video, Heart, Activity, ArrowRight, Users, BookOpen, Lightbulb } from 'lucide-react';
import { supabase } from '../lib/supabase';

const EVENT_SLUG = 'beyond-the-mind';
const ZOOM_URL = 'https://us06web.zoom.us/j/86050009045?pwd=j7DC7bxl8o4NahXpLTpuRsfVxFRubO.1';

const exploreItems = [
  { icon: Sparkles, title: 'Beyond Mindset', body: 'Why physical wellbeing deserves attention alongside mental and emotional change.' },
  { icon: Heart, title: 'My Personal Journey', body: 'What my experience with serious health challenges taught me about taking care of my body.' },
  { icon: Activity, title: 'The 15-30-Second Practice', body: 'A look at the simple wellness technology I use and why I chose to explore it.' },
  { icon: CheckCircle, title: 'Supporting Clients Holistically', body: 'How I introduce optional wellness resources alongside my coaching work.' },
  { icon: Video, title: 'Your Questions', body: 'An opportunity to ask me about my experience and approach.' },
];

export function BeyondTheMindRegistrationPage() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [profession, setProfession] = useState('');
  const [questions, setQuestions] = useState('');
  const [consentMarketing, setConsentMarketing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async () => {
    setError(null);

    if (!fullName.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);

    try {
      const { error: insertError } = await supabase
        .from('masterclass_registrations')
        .insert({
          full_name: fullName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          experience_level: profession.trim() || null,
          questions: questions.trim() || null,
          consent_marketing: consentMarketing,
          event_slug: EVENT_SLUG,
        });

      if (insertError) {
        if (insertError.code === '23505') {
          setError('This email is already registered for this event.');
        } else {
          throw insertError;
        }
        return;
      }

      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            type: 'beyond-the-mind-confirmation',
            recipientEmail: email.trim().toLowerCase(),
            recipientName: fullName.trim(),
            zoomUrl: ZOOM_URL,
          }),
        });
      } catch (emailErr) {
        console.error('Confirmation email failed:', emailErr);
      }

      navigate('/beyondthemind/thank-you');
    } catch (err) {
      console.error('Error registering:', err);
      setError('Something went wrong. Please try again in a moment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-nlp-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-nlp-bg/95 backdrop-blur-lg shadow-sm">
        <div className="max-w-[1216px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-20">
            <button onClick={() => navigate('/home')} className="flex items-center">
              <NLPLogo className="h-12" />
            </button>
            <nav className="hidden md:flex items-center gap-6">
              <button onClick={() => document.getElementById('explore')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                What We'll Explore
              </button>
              <button onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-semibold text-nlp-forest/70 hover:text-nlp-forest transition-colors">
                Register
              </button>
            </nav>
            <Button
              onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
            >
              Reserve Your Place
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 px-4 md:px-8 bg-nlp-bg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-nlp-olive/5 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-nlp-gold/5 rounded-full blur-3xl translate-y-1/4 -translate-x-1/4" />
        <div className="max-w-[900px] mx-auto relative text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <img
              src="/Patrick_Profile_Under_2MB_q95.jpg"
              alt="Patrick Dahdal"
              className="w-14 h-14 rounded-full object-cover border-2 border-nlp-gold/30"
            />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-6">
            <Sparkles className="w-4 h-4 text-nlp-gold-dark" />
            <span className="text-sm font-semibold text-nlp-forest">Free Live Session with Patrick Dahdal</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-nlp-forest mb-4 leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
            Beyond the Mind
          </h1>
          <p className="text-xl md:text-2xl text-nlp-olive mb-6 font-semibold" style={{ fontFamily: 'Georgia, serif' }}>
            The Missing Dimension of Personal Transformation
          </p>
          <p className="text-lg text-nlp-taupe mb-10 max-w-2xl mx-auto leading-relaxed">
            A free live session exploring why physical wellbeing deserves attention alongside mental and emotional change.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button size="lg" onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })} className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full">
              Reserve Your Free Place
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button variant="secondary" size="lg" onClick={() => document.getElementById('explore')?.scrollIntoView({ behavior: 'smooth' })} className="gap-2 bg-nlp-cream text-nlp-forest border border-nlp-cream-dark hover:bg-nlp-cream-dark rounded-full">
              What We'll Explore
            </Button>
          </div>
          {/* Event details */}
          <div className="flex flex-wrap items-center justify-center gap-4">
            <div className="flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-xl">
              <Calendar className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">Friday, October 23, 2026</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-xl">
              <Clock className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">11:00 AM US Eastern | 4:00 PM UK</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-xl">
              <Video className="w-4 h-4 text-nlp-olive" />
              <span className="text-sm font-medium text-nlp-forest">60 Minutes | Live via Zoom</span>
            </div>
          </div>
        </div>
      </section>

      {/* Personal Story */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-bg rounded-full mb-4">
              <Heart className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">A Personal Story</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest" style={{ fontFamily: 'Georgia, serif' }}>
              From Heart Surgery to Celebrating Life — Just Six Weeks Later
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <figure className="bg-nlp-bg rounded-4xl p-4 shadow-soft">
              <img
                src="/images/story/before.jpg"
                alt="Patrick in hospital following double bypass surgery"
                className="w-full aspect-[4/5] object-cover rounded-3xl"
              />
              <figcaption className="text-center text-sm font-semibold text-nlp-forest pt-4 pb-2">
                Following my double bypass surgery.
              </figcaption>
            </figure>
            <figure className="bg-nlp-bg rounded-4xl p-4 shadow-soft">
              <img
                src="/images/story/six_weeks_after.jpg"
                alt="Patrick standing with his arms raised six weeks after surgery"
                className="w-full aspect-[4/5] object-cover rounded-3xl"
              />
              <figcaption className="text-center text-sm font-semibold text-nlp-forest pt-4 pb-2">
                Just six weeks after surgery.
              </figcaption>
            </figure>
          </div>

          <div className="max-w-[800px] mx-auto text-lg text-nlp-taupe leading-relaxed space-y-4">
            <p>A few years ago, I underwent double bypass heart surgery. It was a significant turning point in my life.</p>
            <p>The first photograph shows me following surgery. The second was taken just six weeks later.</p>
            <p>During those six weeks, alongside my medical care and recovery, I was also following a simple 30-second daily wellness protocol.</p>
            <p>That experience deepened my interest in the physical dimension of personal transformation and the importance of looking after the body as well as the mind.</p>
            <p>This Friday, I'll share more about my journey and the simple 30-second protocol I've incorporated into my life.</p>
          </div>

          <div className="text-center mt-10">
            <Button
              size="lg"
              onClick={() => document.getElementById('register')?.scrollIntoView({ behavior: 'smooth' })}
              className="gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
            >
              Reserve My Free Place
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="space-y-4 text-lg text-nlp-taupe leading-relaxed">
            <p>You've learned how powerful the mind can be in creating change. But what about the body?</p>
            <p>As coaches, NLP practitioners, hypnotherapists, and personal development professionals, we spend a great deal of time helping people change their thoughts, emotions, beliefs, and behaviours. Yet there's another important dimension of human potential: supporting physical wellbeing.</p>
            <p>A few years ago, I experienced a major turning point in my own health, including undergoing double bypass surgery. That experience deepened my interest in the relationship between physical wellbeing, daily habits, and human potential.</p>
            <p>Alongside my medical care, I began exploring different wellness approaches. One of the practices I adopted takes only around 15-30 seconds a day. In this special live session, I'll share my personal experience and introduce you to an approach to wellness that has become part of my daily life.</p>
            <p className="text-nlp-forest font-semibold text-xl pt-2">This is the missing dimension.</p>
          </div>
        </div>
      </section>

      {/* What We'll Explore */}
      <section id="explore" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <Lightbulb className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">What We'll Explore</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Five Things We'll Cover Together
            </h2>
          </div>
          <div className="space-y-6">
            {exploreItems.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-nlp-cream rounded-4xl p-8 md:p-10 shadow-soft hover:shadow-soft-lg transition-shadow duration-300">
                  <div className="flex items-start gap-5">
                    <div className="w-14 h-14 rounded-full bg-nlp-forest flex items-center justify-center flex-shrink-0">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-nlp-forest mb-3">{item.title}</h3>
                      <p className="text-nlp-taupe leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Is This For */}
      <section className="py-20 px-4 md:px-8 bg-nlp-forest text-nlp-bg">
        <div className="max-w-[800px] mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-gold/20 rounded-full mb-6">
            <Users className="w-4 h-4 text-nlp-gold-light" />
            <span className="text-sm font-semibold text-nlp-gold-light">Who Is This For?</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Georgia, serif' }}>
            Anyone on a Journey of Personal Development
          </h2>
          <p className="text-lg md:text-xl text-nlp-bg/80 mb-4 leading-relaxed">
            Anyone interested in personal development, NLP, coaching, hypnosis, wellbeing, or exploring complementary approaches to supporting human potential.
          </p>
          <p className="text-xl md:text-2xl text-nlp-gold-light font-semibold pt-4">
            No previous training or experience is necessary.
          </p>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="py-20 md:py-28 px-4 md:px-8 bg-nlp-bg">
        <div className="max-w-[600px] mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-nlp-cream rounded-full mb-4">
              <Calendar className="w-4 h-4 text-nlp-gold-dark" />
              <span className="text-sm font-semibold text-nlp-forest">Free Live Session</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-nlp-forest mb-4" style={{ fontFamily: 'Georgia, serif' }}>
              Reserve Your Free Place
            </h2>
            <p className="text-nlp-taupe">
              Friday, October 23 | 11 AM Eastern / 4 PM UK & Ireland | 60 minutes | Online
            </p>
          </div>

          <div className="bg-nlp-cream rounded-4xl p-8 md:p-10 shadow-soft-lg">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/30 focus:border-nlp-olive transition-colors"
                disabled={submitting}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-nlp-forest mb-1.5">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/30 focus:border-nlp-olive transition-colors"
                disabled={submitting}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                Phone <span className="text-nlp-taupe font-normal">(optional)</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 (555) 123-4567"
                className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/30 focus:border-nlp-olive transition-colors"
                disabled={submitting}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                Your Profession / Role <span className="text-nlp-taupe font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={profession}
                onChange={(e) => setProfession(e.target.value)}
                placeholder="e.g. Coach, NLP Practitioner, Hypnotherapist"
                className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/30 focus:border-nlp-olive transition-colors"
                disabled={submitting}
              />
            </div>

            <div className="mb-5">
              <label className="block text-sm font-semibold text-nlp-forest mb-1.5">
                Questions for Patrick <span className="text-nlp-taupe font-normal">(optional)</span>
              </label>
              <textarea
                value={questions}
                onChange={(e) => setQuestions(e.target.value)}
                placeholder="Is there anything specific you'd like to ask during the session?"
                rows={3}
                className="w-full px-4 py-3 border border-nlp-cream-dark rounded-xl text-sm bg-white text-nlp-forest placeholder:text-nlp-taupe/60 focus:outline-none focus:ring-2 focus:ring-nlp-olive/30 focus:border-nlp-olive transition-colors resize-none"
                disabled={submitting}
              />
            </div>

            <div className="mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentMarketing}
                  onChange={(e) => setConsentMarketing(e.target.checked)}
                  disabled={submitting}
                  className="mt-1 w-5 h-5 rounded border-nlp-cream-dark text-nlp-forest focus:ring-nlp-olive/30 cursor-pointer"
                />
                <span className="text-sm text-nlp-taupe leading-relaxed">
                  I'd like to receive occasional updates about future events and resources from Patrick.
                </span>
              </label>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full gap-2 bg-nlp-forest text-white hover:bg-nlp-forest-light rounded-full"
              size="lg"
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Reserve My Free Place
                </>
              )}
            </Button>

            <p className="text-xs text-nlp-taupe text-center mt-4">
              Complimentary — no cost to attend. You'll receive a Zoom link by email.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 px-4 md:px-8 bg-nlp-cream">
        <div className="max-w-[800px] mx-auto">
          <div className="bg-nlp-bg rounded-4xl p-6 md:p-8">
            <p className="text-sm text-nlp-taupe leading-relaxed text-center">
              This is an educational session based on personal experience and general wellness approaches. Patrick is an independent LifeWave Brand Partner. Products may be discussed and offered at the event. It is not medical advice, and wellness products are not intended to diagnose, treat, cure, or prevent disease.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 md:px-8 bg-nlp-forest-dark text-nlp-bg/60">
        <div className="max-w-[1216px] mx-auto text-center">
          <NLPLogo className="h-10 mx-auto mb-4" />
          <p className="text-sm">NLP OS — Personal Transformation & Wellbeing</p>
        </div>
      </footer>
    </div>
  );
}
